import { DateTime } from 'luxon';

// Diagnosis details
type DiagnosisStatusDefinition = {
  color: string;
  textColor: string;
  title: string;
  value: string;
};
export const diagnosisStatusDefinitions: Record<
  string,
  DiagnosisStatusDefinition
> = {
  Active: {
    color: 'primary.10',
    textColor: 'primary.100',
    title: 'Active',
    value: '1',
  },
  Preliminary: {
    color: 'neutral.background.secondary',
    textColor: 'neutral.secondary',
    title: 'Preliminary',
    value: '4',
  },
  Resolved: {
    color: 'secondary.5',
    textColor: 'secondary.50',
    title: 'Resolved',
    value: '3',
  },
  Void: {
    color: 'red.10',
    textColor: 'red.100',
    title: 'Void',
    value: '2',
  },
  Inactive: {
    color: 'neutral.line',
    textColor: 'neutral.secondary',
    title: 'Inactive',
    value: '5',
  },
};

export enum DiagnosisStatus {
  PRELIMINARY = 'Preliminary',
  ACTIVE = 'Active',
  RESOLVED = 'Resolved',
  VOID = 'Void',
  INACTIVE = 'Inactive',
}

export enum DiagnosisStatusCode {
  PRELIMINARY = 'PRELIMINARY',
  ACTIVE = 'ACTIVE',
  RESOLVED = 'RESOLVED',
  VOID = 'VOID',
  INACTIVE = 'INACTIVE',
}

export const emptyPatientDiagnosis: PatientDiagnosis = {
  createdAt: null,
  diagnosis: null,
  endDate: null,
  externalId: '',
  note: null,
  onsetDate: null,
  primary: false,
  sensitive: false,
  statusCode: DiagnosisStatusCode.PRELIMINARY,
  statusLabel: DiagnosisStatus.PRELIMINARY,
  statusReason: null,
};

export enum AgeRange {
  ELEMENTARY = 'ELEMENTARY',
  TEEN = 'TEEN',
  ADULT = 'ADULT',
  ADULT_18_24 = 'ADULT_18_24',
  ADULT_25_PLUS = 'ADULT_25_PLUS',
}

export const AGE_RANGE_DISPLAY_NAMES: Record<AgeRange, string> = {
  [AgeRange.ELEMENTARY]: 'Elementary (5-11)',
  [AgeRange.TEEN]: 'Teen (12-17)',
  [AgeRange.ADULT]: 'All Adults (18+)',
  [AgeRange.ADULT_18_24]: 'Adult (18-24)',
  [AgeRange.ADULT_25_PLUS]: 'Adult (25+)',
};

export const formatAgeRangeDisplay = (ageRange: string): string => {
  const normalized = String(ageRange).trim().toLowerCase();

  switch (normalized) {
    case 'teen':
    case 'teens':
      return AGE_RANGE_DISPLAY_NAMES[AgeRange.TEEN];
    case 'adult':
      return AGE_RANGE_DISPLAY_NAMES[AgeRange.ADULT];
    case 'adult_18_24':
      return AGE_RANGE_DISPLAY_NAMES[AgeRange.ADULT_18_24];
    case 'adult_25_plus':
      return AGE_RANGE_DISPLAY_NAMES[AgeRange.ADULT_25_PLUS];
    case 'elementary':
      return AGE_RANGE_DISPLAY_NAMES[AgeRange.ELEMENTARY];
    default:
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
};

export const hasPrimaryDiagnosis = (
  diagnoses: PatientDiagnosis[],
  currentDiagnoses?: PatientDiagnosis,
): boolean =>
  diagnoses.some(
    (diagnosis) =>
      diagnosis.primary &&
      currentDiagnoses?.externalId !== diagnosis.externalId,
  );

export const isVoidOrResolvedOrInactive = (
  statusCode: DiagnosisStatusCode,
): boolean =>
  statusCode === DiagnosisStatusCode.RESOLVED ||
  statusCode === DiagnosisStatusCode.VOID ||
  statusCode === DiagnosisStatusCode.INACTIVE;

export const isOpenDiagnosis = (statusCode: DiagnosisStatusCode): boolean =>
  statusCode === DiagnosisStatusCode.ACTIVE ||
  statusCode === DiagnosisStatusCode.PRELIMINARY ||
  statusCode === DiagnosisStatusCode.INACTIVE;

/**
 * Sorting diagnoses based on primary flag,
 * and then by status in the order of preliminary,active,resolved and void
 */
export const getSortedDiagnosisList = (
  diagnoses: PatientDiagnosis[],
): PatientDiagnosis[] => {
  // cloning the array is necessary because sort modifies in-place
  return [...(diagnoses ?? [])].sort((a, b) => {
    const diagnosisStatusValues: string[] = Object.values(DiagnosisStatusCode);
    return (
      Number(b.primary) - Number(a.primary) ||
      diagnosisStatusValues.indexOf(a.statusCode) -
        diagnosisStatusValues.indexOf(b.statusCode)
    );
  });
};

const KG_PER_POUND = 0.453592;
const METERS_PER_INCH = 0.0254;
const INCHES_PER_FOOT = 12;

export const computeBMI = (
  heightData: PatientVitalsObservation,
  weightData: PatientVitalsObservation,
): { bmi: number; datetime: string } => {
  if (heightData && weightData) {
    const { observationDatetime: heightTime, value: heightIn } = heightData;
    const { observationDatetime: weightTime, value: weightLbs } = weightData;
    const weightKg = weightLbs * KG_PER_POUND;
    const heightM = heightIn * METERS_PER_INCH;
    const bmi = weightKg / heightM / heightM;
    const isWeightMostRecent =
      new Date(weightTime).getTime() >= new Date(heightTime).getTime();
    return {
      bmi,
      datetime: isWeightMostRecent ? weightTime : heightTime,
    };
  }
  return {
    bmi: 0,
    datetime: '',
  };
};

export const computeTargetWeight = (
  goalData: PatientWeightGoalType,
  weightData: PatientVitalsObservation,
): { tw: number; datetime: string } => {
  if (goalData && weightData) {
    const { observationDatetime: weightTime, value: weightLbs } = weightData;
    const {
      lowBound: goalLowBound,
      highBound: goalHighBound,
      updatedAt: goalUpdatedTime,
      createdAt: goalCreatedTime,
    } = goalData;

    const targetWeight = goalHighBound
      ? (goalLowBound + goalHighBound) / 2
      : goalLowBound;
    const tw = (weightLbs / targetWeight) * 100;

    const isWeightMostRecent =
      new Date(weightTime).getTime() >=
      new Date(goalUpdatedTime || goalCreatedTime).getTime();
    return {
      datetime: isWeightMostRecent
        ? weightTime
        : goalUpdatedTime || goalCreatedTime,
      tw,
    };
  }
  return {
    datetime: '',
    tw: 0,
  };
};
export const round = (num: number, dec = 1): number =>
  Math.round(num * Math.pow(10, dec)) / Math.pow(10, dec);

const MBMI_AGE_RANGE = {
  MAX: 20,
  MIN: 2,
};

export const getAgeForMBmi = (dob: string): number => {
  if (!dob) return null;
  const age = Math.abs(
    DateTime.fromFormat(dob, 'yyyy-LL-dd').diffNow().as('years'),
  );
  const ageRounded = round(age);
  if (Math.floor(ageRounded) > MBMI_AGE_RANGE.MAX) return null; // if over 21, will not fetch mBMI
  if (ageRounded > MBMI_AGE_RANGE.MAX) return MBMI_AGE_RANGE.MAX; // if between 20 and 21, use 20
  return Math.max(ageRounded, MBMI_AGE_RANGE.MIN);
};

export const formatHeight = (height: number): string => {
  const feet = Math.floor(height / INCHES_PER_FOOT);
  const inches = height % INCHES_PER_FOOT;
  const roundedInches = Math.round((inches + Number.EPSILON) * 10) / 10;
  return `${feet}' ${roundedInches}"`;
};

/** Checks if the code received from survey is already present in the diagnosis response.
 * If present the code from survey will not be added
 */
export const filterSurveyDiagnosisCodesToBeAdded = (
  diagnosesFromSurvey: DiagnosisCode[],
  diagnosesResponseCodes: string[],
): PatientDiagnosis[] => {
  const res: PatientDiagnosis[] = [];

  diagnosesFromSurvey?.forEach((diagnosisCode: DiagnosisCode) => {
    if (!(diagnosesResponseCodes || []).includes(diagnosisCode.code)) {
      res.push({
        ...emptyPatientDiagnosis,
        diagnosis: diagnosisCode,
        isAddedFromSurvey: true,
      });
    }
  });
  return res;
};

export const getMedianGoalWeight = (
  latestActiveWeightGoal: PatientWeightGoalType,
) => {
  return latestActiveWeightGoal && latestActiveWeightGoal?.lowBound
    ? `${
        latestActiveWeightGoal?.highBound
          ? (latestActiveWeightGoal?.lowBound +
              latestActiveWeightGoal?.highBound) /
            2
          : latestActiveWeightGoal?.lowBound
      } lbs`
    : 'NA';
};

export const isAllAddressFieldsNotEmpty = ({
  addressLine1,
  city,
  state,
  zipCode,
}) => {
  return addressLine1 && city && state && zipCode;
};

export const shouldShowActionBar = (age: number): boolean => {
  return age >= 18;
};
export const REORDER_API_REASON_CODES = new Set([
  'DEFECTIVE',
  'LOST_IN_TRANSIT',
  'STOLEN',
  'DAMAGED_BEFORE_DELIVERY',
]);

export const isReorderApiReason = (reasonCode: string): boolean =>
  REORDER_API_REASON_CODES.has(reasonCode);
